import React from 'react';
import type { OrderId } from '@seaber/common/graphql/types';
import type { ScenarioTimeline_OrderFragment } from '../../graphql';
import { formatNumber, pluralize } from '../../lib/format';

export interface OrderSummary {
  readonly id: OrderId;
  readonly externalOrderId: string | null;
  readonly loadPort: string | null;
  readonly dischargePort: string | null;
  readonly cargoAmount: number | null;
  readonly cargoUnit: string | null;
  readonly commodity: string | null;
}

const MAX_VISIBLE_ORDERS = 5 as const;

export function mapToOrderSummary(
  order: ScenarioTimeline_OrderFragment,
): OrderSummary {
  return {
    id: order.id,
    externalOrderId: order.externalOrderId ?? null,
    loadPort: order.locationByLoadPortId?.name ?? null,
    dischargePort: order.locationByDischargePortId?.name ?? null,
    cargoAmount: order.cargoAmount != null ? Number(order.cargoAmount) : null,
    cargoUnit: order.cargoUnitByCargoUnitId?.name ?? null,
    commodity: order.assortmentByAssortmentId?.name ?? null,
  };
}

function formatRoute(order: OrderSummary): string | null {
  if (order.loadPort && order.dischargePort) {
    return `${order.loadPort} \u2192 ${order.dischargePort}`;
  }
  return order.loadPort ?? order.dischargePort ?? null;
}

function formatCargo(order: OrderSummary): string | null {
  if (order.cargoAmount == null) return null;
  const parts: string[] = [];
  const formatted = formatNumber(order.cargoAmount);
  if (formatted) parts.push(formatted);
  if (order.cargoUnit) parts.push(order.cargoUnit);
  if (order.commodity) parts.push(order.commodity);
  return parts.length > 0 ? parts.join(' ') : null;
}

function OrderCard({
  order,
  action,
}: {
  order: OrderSummary;
  action: 'created' | 'updated';
}) {
  const label = `#${order.id}`;
  const route = formatRoute(order);
  const cargo = formatCargo(order);
  const badgeColor = action === 'created' ? '#2e7d32' : '#1565c0';

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '6px',
        padding: '8px 10px',
        marginBottom: '6px',
        border: '1px solid rgba(0,0,0,0.08)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: route || cargo ? '4px' : 0,
        }}
      >
        <span
          style={{
            fontSize: '10px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: '#fff',
            background: badgeColor,
            borderRadius: '3px',
            padding: '1px 5px',
            lineHeight: '16px',
            flexShrink: 0,
          }}
        >
          {action}
        </span>
        <span style={{ fontWeight: 600, fontSize: '13px' }}>{label}</span>
      </div>
      {(route || cargo) && (
        <div
          style={{
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            paddingLeft: '2px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2px 12px',
          }}
        >
          {route && <span>{route}</span>}
          {cargo && <span>{cargo}</span>}
        </div>
      )}
    </div>
  );
}

interface IntegrationOrdersToastProps {
  readonly insertedOrders: ReadonlyArray<OrderSummary>;
  readonly updatedOrders: ReadonlyArray<OrderSummary>;
}

const IntegrationOrdersToast: React.FC<IntegrationOrdersToastProps> = ({
  insertedOrders,
  updatedOrders,
}) => {
  const headerParts: string[] = [];
  if (insertedOrders.length > 0) {
    headerParts.push(pluralize`${insertedOrders.length} order` + ' created');
  }
  if (updatedOrders.length > 0) {
    headerParts.push(pluralize`${updatedOrders.length} order` + ' updated');
  }

  const allOrders: ReadonlyArray<{
    order: OrderSummary;
    action: 'created' | 'updated';
  }> = [
    ...insertedOrders.map((order) => ({ order, action: 'created' as const })),
    ...updatedOrders.map((order) => ({ order, action: 'updated' as const })),
  ];
  const visible = allOrders.slice(0, MAX_VISIBLE_ORDERS);
  const remaining = allOrders.length - visible.length;

  return (
    <div style={{ fontFamily: 'inherit' }}>
      <div
        style={{
          fontWeight: 600,
          fontSize: '13px',
          marginBottom: '8px',
        }}
      >
        {headerParts.join(', ')} via integration
      </div>
      {visible.map(({ order, action }) => (
        <OrderCard key={order.id} order={order} action={action} />
      ))}
      {remaining > 0 && (
        <div
          style={{
            fontSize: '12px',
            color: 'rgba(0,0,0,0.5)',
            marginTop: '2px',
          }}
        >
          +{remaining} more
        </div>
      )}
    </div>
  );
};

export function createIntegrationOrdersToast(
  insertedOrders: ReadonlyArray<OrderSummary>,
  updatedOrders: ReadonlyArray<OrderSummary>,
): React.ReactElement {
  return (
    <IntegrationOrdersToast
      insertedOrders={insertedOrders}
      updatedOrders={updatedOrders}
    />
  );
}

export default IntegrationOrdersToast;
